import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";

import {
  DeleteTeeSheetNotification,
  GetTeeSheetNotification,
  GetTeeSheetNotificationGroup,
  PutTeeSheetNotification,
  TTeesheetNotificationType,
} from "api/rpc/2024-04/facilityAdmin/teesheet/notification";

import { showError } from "redux/actions/ui";
import { TTeesheetNotification } from "redux/reducers/models/teesheet";
import { useAppDispatch, useAppSelector } from "hooks/redux";

import Page from "components/page/Page";
import Card from "components/card/Card";
import Popup from "components/popup/Popup";
import Input from "components/form/input/Input";
import FormLayout from "components/form/FormLayout";
import { Select } from "components/select";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import useUnsavedChangesContainer from "hooks/useUnsavedChangesContainer/useUnsavedChangesContainer";
import { isEmpty } from "helpers/Helpers";
import TextEditor from "components/textEditor/textEditor";

import "./teeSheetNotification.scss";

interface ITeeSheetNotificationSaveState {
  startDate: Date;
  endDate: Date;
  content: string;
  courseId: number;
  id: string;
  notificationGroupId?: string | number;
  subject: string;
  type: string;
  userType: TTeesheetNotificationType;
}

interface INotificationUserTypeOption {
  label: string;
  value: TTeesheetNotificationType;
}

export default function TeeSheetNotification() {
  const { notificationId, notificationGroupId } = useParams<{ notificationId?: string; notificationGroupId?: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const isGroupMode = notificationGroupId != null;

  const NO_COURSE_ID = -1;

  const dispatch = useAppDispatch();
  const facilityStore = useAppSelector(store => store.facilityStore);

  const notificationUserTypeOptions: INotificationUserTypeOption[] = useMemo(() => {
    return [
      {
        label: t("Internal"),
        value: "admin",
      },
      {
        label: t("External"),
        value: "customer",
      },
    ];
  }, []);

  const [deletePopupVisible, setDeletePopupVisible] = useState<boolean>(false);
  const [groupNotifications, setGroupNotifications] = useState<TTeesheetNotification[]>([]);
  const [loadedNotification, setLoadedNotification] = useState<boolean>(false);

  const [saveStateContainer, saveStateContainerActions] = useUnsavedChangesContainer<ITeeSheetNotificationSaveState>(
    (value, other) => {
      if (value instanceof Date && other instanceof Date) {
        return (
          value.getFullYear() === other.getFullYear() &&
          value.getMonth() === other.getMonth() &&
          value.getDate() === other.getDate()
        );
      } else {
        return undefined;
      }
    },
  );

  // populate component
  useEffect(() => {
    setLoadedNotification(false);
    setGroupNotifications([]);
    void loadNotification();
  }, [notificationId, notificationGroupId]);

  /** Formatted String ==> YYYY-MM-DD */
  function dateFromFormattedString(formattedString: string): Date {
    const tempDateArray = formattedString.split("-");

    try {
      const returnDate = new Date(Number(tempDateArray[0]), Number(tempDateArray[1]) - 1, Number(tempDateArray[2]));
      return returnDate;
    } catch (error) {
      console.log(error);
      return new Date();
    }
  }

  async function loadNotification() {
    if (isGroupMode) {
      await loadNotificationGroup();
      return;
    }

    if (!notificationId) {
      return;
    }

    const tempRes = await GetTeeSheetNotification({ id: notificationId }, true);

    if (tempRes.status !== StatusCode.OK) {
      return;
    }

    const saveState = buildSaveState(tempRes.data, notificationId);

    saveStateContainerActions.set(saveState);

    setLoadedNotification(true);
  }

  async function loadNotificationGroup() {
    if (!notificationGroupId) {
      return;
    }

    const response = await GetTeeSheetNotificationGroup({ notification_group_id: notificationGroupId }, true);

    if (response.status !== StatusCode.OK) {
      dispatch(showError(response.data as unknown as string));
      return;
    }

    if (response.data.length === 0) {
      dispatch(showError(t("No notifications found in this group.")));
      return;
    }

    const matchingNotifications = [...response.data].sort((firstNotification, secondNotification) => {
      return momentDateValue(firstNotification.start_date) - momentDateValue(secondNotification.start_date);
    });

    const primaryNotification = matchingNotifications[0];
    const groupStartDate = matchingNotifications[0].start_date;
    const groupEndDate = matchingNotifications.reduce((latestEndDate, notification) => {
      return momentDateValue(notification.end_date) > momentDateValue(latestEndDate) ? notification.end_date : latestEndDate;
    }, matchingNotifications[0].end_date);

    const saveState = buildSaveState(primaryNotification, primaryNotification.id, {
      startDate: groupStartDate,
      endDate: groupEndDate,
    });

    setGroupNotifications(matchingNotifications);
    saveStateContainerActions.set(saveState);
    setLoadedNotification(true);
  }

  function buildSaveState(
    notification: TTeesheetNotification,
    id: string | number,
    dateRange?: { startDate: string; endDate: string },
  ): ITeeSheetNotificationSaveState {
    return {
      startDate: dateFromFormattedString(dateRange?.startDate ?? notification.start_date),
      endDate: dateFromFormattedString(dateRange?.endDate ?? notification.end_date),
      content: notification.content,
      courseId: notification.course_id ?? NO_COURSE_ID,
      id: String(id),
      notificationGroupId: notification.notification_group_id,
      subject: notification.subject,
      type: notification.type,
      userType: notification.user_type,
    };
  }

  function momentDateValue(date: string) {
    return dateFromFormattedString(date).getTime();
  }

  async function saveNotification() {
    if (isEmpty(saveStateContainer.updatedState?.subject) || isEmpty(saveStateContainer.updatedState?.content)) {
      return;
    }

    const res = await PutTeeSheetNotification(
      {
        id: Number(saveStateContainer.updatedState?.id),
        update_group: isGroupMode ? true : undefined,
        start_date: isGroupMode ? undefined : saveStateContainer.updatedState?.startDate.toISOString().slice(0, 10),
        end_date: isGroupMode ? undefined : saveStateContainer.updatedState?.endDate.toISOString().slice(0, 10),
        course_id:
          saveStateContainer.updatedState?.courseId === NO_COURSE_ID ? undefined : saveStateContainer.updatedState?.courseId,
        subject: saveStateContainer.updatedState?.subject,
        content: saveStateContainer.updatedState?.content,
        user_type: saveStateContainer.updatedState?.userType,
        type: saveStateContainer.updatedState?.type,
      },
      true,
    );

    if (res.status !== StatusCode.OK) {
      dispatch(showError(res.data as string));
      return;
    }

    saveStateContainerActions.save();

    history.push("/admin/settings/tee-sheet/notification");
  }

  function cancelUnsavedChanges() {
    saveStateContainerActions.rollback();
  }

  async function handleNotificationDelete() {
    const teeSheetNotificationId = saveStateContainer.updatedState?.id ?? notificationId;

    if (!teeSheetNotificationId) {
      return;
    }

    const deleteRes = await DeleteTeeSheetNotification(
      { id: teeSheetNotificationId, delete_group: isGroupMode ? true : undefined },
      true,
    );

    if (deleteRes.status !== StatusCode.OK) {
      dispatch(showError(deleteRes.data as string));
      return;
    }

    history.push("/admin/settings/tee-sheet/notification");
  }

  const primaryAction = {
    content: t("Delete"),
    action: () => setDeletePopupVisible(true),
  };

  const pageTitle = isGroupMode
    ? `${t("Tee Sheet Notification Group")}`
    : `${t("Tee Sheet Notification")} #${String(notificationId)}`;

  return (
    <Page
      title={pageTitle}
      narrow
      breadcrumbs={[
        {
          prefix: true,
          label: t("Back to notifications"),
          url: "/admin/settings/tee-sheet/notification",
        },
      ]}
      primaryAction={primaryAction}
      notificationBarProps={{
        isVisible: saveStateContainer.changesExist,
        onAction: saveNotification,
        onCancel: cancelUnsavedChanges,
      }}
    >
      {loadedNotification && (
        <Card>
          <Card.Section>
            <FormLayout>
              {isGroupMode && saveStateContainer.updatedState?.startDate && saveStateContainer.updatedState?.endDate && (
                <FormLayout.Group>
                  <div className="notification-group-summary">
                    <p className="notification-group-summary-label">{t("Notification Group")}</p>
                    <p className="notification-group-summary-value">
                      {t("This group includes")} {groupNotifications.length} {t("notifications from")}{" "}
                      {saveStateContainer.updatedState.startDate.toLocaleDateString()} {t("to")}{" "}
                      {saveStateContainer.updatedState.endDate.toLocaleDateString()}.
                    </p>
                    <p className="notification-group-summary-help">
                      {t("Dates are managed by the individual notifications in this group.")}
                    </p>
                  </div>
                </FormLayout.Group>
              )}

              {!isGroupMode && saveStateContainer.updatedState?.startDate && saveStateContainer.updatedState?.endDate && (
                <FormLayout.Group>
                  <DatePickerInput
                    label={t("Notification Date Range")}
                    months={1}
                    position="left"
                    startingDate={saveStateContainer.updatedState.startDate}
                    setStartingDate={(startDate: Date) =>
                      saveStateContainerActions.update(prev => ({ ...prev, startDate }))
                    }
                    endingDate={saveStateContainer.updatedState.endDate}
                    setEndingDate={(endDate: Date) =>
                      saveStateContainerActions.update(prev => ({ ...prev, endDate }))
                    }
                  />
                </FormLayout.Group>
              )}

              {facilityStore.facility?.courses?.length > 1 && (
                <FormLayout.Group>
                  <Select
                    label={t("Is this notification specific to a course?")}
                    onChange={(courseId: number) => {
                      saveStateContainerActions.update(prev => ({ ...prev, courseId }));
                    }}
                    defaultValue={saveStateContainer.updatedState?.courseId}
                  >
                    <Select.Option value={NO_COURSE_ID} name={"None"}>
                      &nbsp;
                    </Select.Option>
                    {facilityStore.facility?.courses?.map(course => {
                      return (
                        <Select.Option key={course.id} value={course.id} name={course.full_name}>
                          {course.full_name}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </FormLayout.Group>
              )}
              <FormLayout.Group>
                <Select
                  label={t("Notification Type")}
                  onChange={(userType: TTeesheetNotificationType) =>
                    saveStateContainerActions.update(prev => ({ ...prev, userType }))
                  }
                  defaultValue={saveStateContainer.updatedState?.userType}
                >
                  {notificationUserTypeOptions.map((option, index: number) => {
                    return (
                      <Select.Option key={index} value={option.value} name={option.label}>
                        {option.label}
                      </Select.Option>
                    );
                  })}
                </Select>
              </FormLayout.Group>
              <FormLayout.Group>
                <Input
                  value={saveStateContainer.updatedState?.subject}
                  label={t("Subject")}
                  onChange={e => saveStateContainerActions.update(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder={t("Subject")}
                  error={isEmpty(saveStateContainer.updatedState?.subject)}
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <TextEditor
                  markdownText={saveStateContainer.updatedState?.content || ""}
                  markdownTextOnChange={newContent =>
                    saveStateContainerActions.update(prev => ({ ...prev, content: newContent }))
                  }
                  label={t("Contents")}
                  error={isEmpty(saveStateContainer.updatedState?.content)}
                />
              </FormLayout.Group>
            </FormLayout>
          </Card.Section>
        </Card>
      )}

      <Popup
        onCancel={() => setDeletePopupVisible(false)}
        onOk={handleNotificationDelete}
        cancelText={t("Close")}
        okText={t("Delete")}
        open={deletePopupVisible}
        type="warning"
        title={isGroupMode ? t("Confirm Notification Group Delete") : t("Confirm Notification Delete")}
        description={
          isGroupMode
            ? t("Are you sure you want to remove all notifications in this group?")
            : t("Are you sure you want to remove this notification?")
        }
      />
    </Page>
  );
}
